import { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';
import { useTheme } from '../../contexts/ThemeContext';
import { KeyCountWrapper } from '../../models/ApiModels';

interface Props {
  series: KeyCountWrapper[];
  labels?: string[];
  hideLabels?: boolean;
  width?: number;
  compare: boolean;
  colorSetting?: 'yellow' | 'blue' | 'connected' | 'curveArray';
  xAxisTitle?: string;
  yAxisTitle?: string;
}

type TooltipFormatterOptions = {
  seriesIndex: number;
  dataPointIndex: number;
  w?: {
    config?: {
      series?: Array<{
        data?: Array<{
          metaDiff?: number | null;
        }>;
      }>;
    };
  };
};

function ColumnChart(props: Props) {
  const { resolvedTheme } = useTheme();

  const toFiniteNumber = (value: unknown) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;

    if (typeof value === 'string') {
      const parsedValue = Number(value);
      if (Number.isFinite(parsedValue)) return parsedValue;
    }

    return null;
  };

  const points = props.series.map((serie, index) => {
    const labelFromProps = props.labels?.[index]?.trim();
    const labelFromSeries = serie.key?.trim();
    const label = labelFromProps || labelFromSeries || `Category ${index + 1}`;
    const count = toFiniteNumber(serie.count) ?? 0;
    const diff = toFiniteNumber(serie.diff);

    return {
      x: label,
      y: count,
      metaDiff: diff,
    };
  });

  function handleThemeChange() {
    return resolvedTheme === 'light' ? '#374151' : '#D1D5DB';
  }

  const colorsBlue = [
    '#CDF2F6',
    '#9BE6EE',
    '#6AD9E5',
    '#38CDDD',
    '#06C0D4',
    '#0A6670',
    '#053338',
    '#642814',
    '#CA5128',
    '#F56432',
    '#F77E55',
    '#F9A385',
  ];

  const colorsYellow = [
    '#FFF4CC',
    '#FFE08A',
    '#FFCC55',
    '#FFB900',
    '#E6A300',
    '#997302',
    '#665001',
    '#332800',
    '#642814',
    '#CA5128',
    '#F56432',
    '#F77E55',
  ];

  const colorsConnected = [
    '#CDF2F6',
    '#9BE6EE',
    '#6AD9E5',
    '#38CDDD',
    '#06C0D4',
    '#FFCC55',
    '#FC6363',
    '#AA0303',
    '#642814',
    '#CA5128',
    '#F56432',
    '#F77E55',
  ];

  const colorsCurveArray = Array.from({ length: 100 }, (_, i) => {
    const ratio = i / 99;
    const r = Math.round(205 * (1 - ratio) + 10 * ratio);
    const g = Math.round(192 * (1 - ratio) + 192 * ratio);
    const b = Math.round(246 * (1 - ratio) + 212 * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  });

  const handleColorSetting = () => {
    switch (props.colorSetting) {
      case 'blue':
        return colorsBlue;
      case 'yellow':
        return colorsYellow;
      case 'connected':
        return colorsConnected;
      case 'curveArray':
        return colorsCurveArray;
      default:
        return colorsBlue;
    }
  };

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        columnWidth: '90%',
        distributed: true,
      },
    },
    colors: handleColorSetting(),
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: false,
    },
    grid: {
      show: false,
    },
    xaxis: {
      categories: points.map((point) => point.x),
      labels: {
        show: props.hideLabels === true ? false : true,
        style: {
          colors: handleThemeChange(),
          fontSize: '12px',
        },
      },
      title: {
        style: {
          color: handleThemeChange(),
        },
        text: props.xAxisTitle,
      },
      axisTicks: {
        show: false,
      },
    },
    yaxis: {
      labels: {
        show: props.hideLabels === true ? false : true,
        style: {
          colors: handleThemeChange(),
          fontSize: '12px',
        },
        formatter: (value) => {
          return value.toLocaleString('en-US');
        },
      },
      title: {
        style: {
          color: handleThemeChange(),
        },
        text: props.yAxisTitle,
      },
      axisTicks: {
        show: false,
      },
    },
    tooltip: {
      theme: resolvedTheme === 'light' ? 'light' : 'dark',
      y: {
        formatter: (value: number, opts: TooltipFormatterOptions) => {
          if (!props.compare) return `${value.toLocaleString('en-US')}`;

          const diff = opts?.w?.config?.series?.[opts.seriesIndex]?.data?.[opts.dataPointIndex]?.metaDiff;
          if (typeof diff !== 'number' || !Number.isFinite(diff)) return `${value} (N/A)`;

          const sign = diff > 0 ? '+' : '';
          return `${value.toLocaleString('en-US')} (${sign}${diff})`;
        },
      },
    },
    legend: {
      show: false,
    },
  };

  const series = [
    {
      name: 'Count',
      data: points,
    },
  ];

  return (
    <div className='flex justify-center prose-labelStandard'>
      <Chart options={options} series={series} type='bar' width={400} />
    </div>
  );
}

export default ColumnChart;
